import { useCallback, useEffect, useRef, type ComponentPropsWithoutRef } from 'react'
import {
  FormProvider,
  useFormContext,
  type UseFormReturn,
  type FieldValues,
  type SubmitHandler,
  type FieldErrors,
} from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import clsx from 'clsx'
import * as styles from './Form.css'
import { scrollToFirstError } from './helpers'

// =============================================================================
// Form Component
// =============================================================================

type FormProps<T extends FieldValues> = {
  /** Form methods from useForm() */
  form: UseFormReturn<T>
  /** Submit handler */
  onSubmit: SubmitHandler<T>
  /** Form children */
  children: React.ReactNode
} & Omit<ComponentPropsWithoutRef<'form'>, 'onSubmit'>

/**
 * Form wrapper that provides react-hook-form context to children.
 * Automatically scrolls to and focuses the first field with an error on validation failure.
 *
 * @example
 * ```tsx
 * const form = useForm<FormData>({
 *   resolver: zodResolver(schema),
 * })
 *
 * <Form form={form} onSubmit={handleSubmit}>
 *   <Input name="email" control={form.control} label="Email" />
 *   <Input name="password" control={form.control} label="Password" />
 *   <Form.Error />
 *   <Button type="submit">Submit</Button>
 * </Form>
 * ```
 */
function FormRoot<T extends FieldValues>({
  form,
  onSubmit,
  children,
  ...props
}: FormProps<T>) {
  const handleInvalid = useCallback(() => {
    scrollToFirstError()
  }, [])

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, handleInvalid)} {...props}>
        {children}
      </form>
    </FormProvider>
  )
}

// =============================================================================
// Form.Error Component
// =============================================================================

type FormErrorProps = {
  /**
   * Field name to show error for.
   * - Use 'root' for form-level errors (API errors, etc.)
   * - Use field name for field-specific errors
   * @default 'root'
   */
  name?: string
  /**
   * Visual variant
   * - 'inline': Simple text error (default)
   * - 'box': Error in a colored box
   */
  variant?: 'inline' | 'box'
  /** Additional CSS class */
  className?: string
}

/**
 * Displays form error message for a specific field or root error.
 * Uses @hookform/error-message under the hood.
 *
 * @example
 * ```tsx
 * // Form-level error (API errors, auth errors, etc.)
 * <Form.Error />
 * <Form.Error name="root" />
 *
 * // Field-level error (usually handled by Input component)
 * <Form.Error name="email" />
 *
 * // Box variant for prominent errors
 * <Form.Error variant="box" />
 * ```
 */
function FormError({ name = 'root', variant = 'inline', className }: FormErrorProps) {
  const { formState: { errors } } = useFormContext()
  const errorRef = useRef<HTMLParagraphElement>(null)

  // Get the error message for this field
  const errorMessage = name === 'root'
    ? (errors.root?.message as string | undefined)
    : (errors[name]?.message as string | undefined)

  // Scroll to error when it appears
  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [errorMessage])

  return (
    <ErrorMessage
      name={name}
      errors={errors as FieldErrors}
      render={({ message }) => (
        <p
          ref={errorRef}
          className={clsx(
            variant === 'box' ? styles.errorBox : styles.error,
            className
          )}
          role="alert"
          data-variant={variant}
        >
          {message}
        </p>
      )}
    />
  )
}

// =============================================================================
// Form.Field Component
// =============================================================================

type FormFieldProps = {
  /** Field name - used to display the error for this field */
  name: string
  /** Field content (Input, Combobox, etc.) */
  children: React.ReactNode
  /** Additional CSS class for the wrapper */
  className?: string
}

/**
 * Get the first error message from a field, including nested fields.
 * For compound components like AddressInput, this will find the first
 * nested error (e.g., address.streetAddress, address.city).
 */
function getFirstErrorMessage(errors: FieldErrors, name: string): string | undefined {
  // Try direct field error first
  const directError = errors[name]
  if (directError?.message && typeof directError.message === 'string') {
    return directError.message
  }

  // If no direct error, check for nested errors (compound components)
  if (directError && typeof directError === 'object' && !('message' in directError && 'type' in directError)) {
    // It's a nested error object, find first error message
    for (const key of Object.keys(directError)) {
      const nestedError = (directError as Record<string, unknown>)[key]
      if (nestedError && typeof nestedError === 'object' && 'message' in nestedError) {
        const message = (nestedError as { message?: unknown }).message
        if (typeof message === 'string') {
          return message
        }
      }
    }
  }

  return undefined
}

/**
 * Wrapper that displays error message for a field.
 * Wrap any form input component to show its validation errors.
 * Supports nested errors for compound components like AddressInput.
 *
 * @example
 * ```tsx
 * <Form.Field name="email">
 *   <Input name="email" control={form.control} label="Email" />
 * </Form.Field>
 *
 * <Form.Field name="address">
 *   <AddressInput name="address" control={form.control} />
 * </Form.Field>
 * ```
 */
function FormField({ name, children, className }: FormFieldProps) {
  const { formState: { errors } } = useFormContext()
  const errorMessage = getFirstErrorMessage(errors, name)

  return (
    <div className={className}>
      {children}
      {errorMessage && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  )
}

// =============================================================================
// Compound Component Export
// =============================================================================

export const Form = Object.assign(FormRoot, {
  Error: FormError,
  Field: FormField,
})
